import { useState, useRef, useEffect } from 'react';
import { trpc } from '../lib/trpc';

// Admin role type from the API
type AdminRole = 'super_admin' | 'support' | 'marketing';

// Role information for display
const roleInfo: Record<AdminRole, { label: string; color: string; bg: string; description: string }> = {
  super_admin: {
    label: 'Super Admin',
    color: '#5A6A8A',
    bg: 'rgba(90, 106, 138, 0.15)',
    description: 'Full access to all features and settings'
  },
  support: {
    label: 'Support',
    color: '#5A8A5E',
    bg: 'rgba(90, 138, 94, 0.15)',
    description: 'Access to Users, Safety Monitor, Support Inbox, Media Library'
  },
  marketing: {
    label: 'Marketing',
    color: '#9B7B3C',
    bg: 'rgba(232, 200, 140, 0.15)',
    description: 'Access to Revenue metrics and Media Library'
  },
};

// Settings section type
type SettingsSection = 'general' | 'team' | 'billing' | 'api' | 'notifications' | 'developer';

// Alert threshold options
type AlertThreshold = 'low' | 'medium' | 'high';

// Static environment settings (not stored in database)
const staticSettings: {
  environment: 'development' | 'production';
  apiUrl: string;
  apiVersion: string;
} = {
  environment: 'development',
  apiUrl: 'http://localhost:3000',
  apiVersion: 'v1',
};

// Integration type from API
type IntegrationType = 'stripe' | 'telegram';

// Integration type definition for UI
type Integration = {
  id: IntegrationType;
  name: string;
  description: string;
  icon: string;
  connected: boolean;
  enabled: boolean;
  requiresApiKey: boolean;
  requiresWebhookUrl: boolean;
  apiKeyPlaceholder?: string;
  webhookUrlPlaceholder?: string;
  helpUrl?: string;
  hasApiKey?: boolean;
  maskedApiKey?: string;
  hasWebhookUrl?: boolean;
  maskedWebhookUrl?: string;
  verifiedAt?: string;
};

/**
 * Settings page component with sidebar navigation.
 *
 * Displays application settings and team management with a sidebar layout.
 */
export function SettingsPage() {
  // Active section state
  const [activeSection, setActiveSection] = useState<SettingsSection>('general');

  // Team management state
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<AdminRole | ''>('');
  const [offset, setOffset] = useState(0);
  const limit = 10;

  // Modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationStep, setVerificationStep] = useState<1 | 2>(1);

  // Invite form state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState<AdminRole>('support');
  const [inviteError, setInviteError] = useState('');

  // Edit role modal state
  const [showEditRoleModal, setShowEditRoleModal] = useState(false);
  const [editRoleAdmin, setEditRoleAdmin] = useState<{ id: string; name: string | null; email: string; role: string } | null>(null);
  const [editRoleNewRole, setEditRoleNewRole] = useState<AdminRole>('support');
  const [editRoleError, setEditRoleError] = useState('');

  // Verification code state
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const codeInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Modal refs for click outside detection
  const inviteModalRef = useRef<HTMLDivElement>(null);
  const verificationModalRef = useRef<HTMLDivElement>(null);
  const editRoleModalRef = useRef<HTMLDivElement>(null);

  // Helper functions for closing modals
  const handleCloseInviteModal = () => {
    setShowInviteModal(false);
    setInviteEmail('');
    setInviteName('');
    setInviteRole('support');
    setInviteError('');
  };

  const handleCloseVerificationModal = () => {
    setShowVerificationModal(false);
    setVerificationStep(1);
    setVerificationCode(['', '', '', '', '', '']);
  };

  const handleCloseEditRoleModal = () => {
    setShowEditRoleModal(false);
    setEditRoleAdmin(null);
    setEditRoleNewRole('support');
    setEditRoleError('');
  };

  // Developer tools state
  const [devToolsMessage, setDevToolsMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // General settings form state
  const [appName, setAppName] = useState('');
  const [autoModerationEnabled, setAutoModerationEnabled] = useState(true);
  const [alertThreshold, setAlertThreshold] = useState<AlertThreshold>('medium');
  const [summaryModel, setSummaryModel] = useState('');
  const [settingsInitialized, setSettingsInitialized] = useState(false);
  const [settingsSaveMessage, setSettingsSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Notification preferences state
  const [notificationPrefsMessage, setNotificationPrefsMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Integrations state
  const [showIntegrationModal, setShowIntegrationModal] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  const [integrationApiKey, setIntegrationApiKey] = useState('');
  const [integrationWebhookUrl, setIntegrationWebhookUrl] = useState('');
  const [integrationError, setIntegrationError] = useState('');
  const [integrationValidating, setIntegrationValidating] = useState(false);
  const integrationModalRef = useRef<HTMLDivElement>(null);

  const utils = trpc.useUtils();

  // Settings query
  const settingsQuery = trpc.settings.get.useQuery();

  // Settings mutation
  const updateSettingsMutation = trpc.settings.update.useMutation({
    onSuccess: () => {
      utils.settings.get.invalidate();
      setSettingsSaveMessage({ type: 'success', text: 'Settings saved successfully' });
      setTimeout(() => setSettingsSaveMessage(null), 3000);
    },
    onError: (error: { message: string }) => {
      setSettingsSaveMessage({ type: 'error', text: `Failed to save settings: ${error.message}` });
    },
  });

  // Notification preferences query and mutation
  const notificationPrefsQuery = trpc.admin.getPreferences.useQuery();

  const updateNotificationPrefsMutation = trpc.admin.updatePreferences.useMutation({
    onSuccess: () => {
      utils.admin.getPreferences.invalidate();
      setNotificationPrefsMessage({ type: 'success', text: 'Notification preferences updated' });
      setTimeout(() => setNotificationPrefsMessage(null), 3000);
    },
    onError: (error: { message: string }) => {
      setNotificationPrefsMessage({ type: 'error', text: `Failed to update preferences: ${error.message}` });
    },
  });

  // Initialize form state from query data
  useEffect(() => {
    if (settingsQuery.data && !settingsInitialized) {
      setAppName(settingsQuery.data.appName);
      setAutoModerationEnabled(settingsQuery.data.autoModerationEnabled);
      setAlertThreshold(settingsQuery.data.alertThreshold as AlertThreshold);
      setSummaryModel(settingsQuery.data.summaryModel || '');
      setSettingsInitialized(true);
    }
  }, [settingsQuery.data, settingsInitialized]);

  // Helper function to close integration modal
  const handleCloseIntegrationModal = () => {
    setShowIntegrationModal(false);
    setSelectedIntegration(null);
    setIntegrationApiKey('');
    setIntegrationWebhookUrl('');
    setIntegrationError('');
    setIntegrationValidating(false);
  };

  // Handle modal keyboard and click events
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showVerificationModal) {
          handleCloseVerificationModal();
        } else if (showInviteModal) {
          handleCloseInviteModal();
        } else if (showIntegrationModal) {
          handleCloseIntegrationModal();
        } else if (showEditRoleModal) {
          handleCloseEditRoleModal();
        }
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (showVerificationModal && verificationModalRef.current && !verificationModalRef.current.contains(e.target as Node)) {
        handleCloseVerificationModal();
      } else if (showInviteModal && inviteModalRef.current && !inviteModalRef.current.contains(e.target as Node)) {
        handleCloseInviteModal();
      } else if (showIntegrationModal && integrationModalRef.current && !integrationModalRef.current.contains(e.target as Node)) {
        handleCloseIntegrationModal();
      } else if (showEditRoleModal && editRoleModalRef.current && !editRoleModalRef.current.contains(e.target as Node)) {
        handleCloseEditRoleModal();
      }
    };

    if (showInviteModal || showVerificationModal || showIntegrationModal || showEditRoleModal) {
      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [showInviteModal, showVerificationModal, showIntegrationModal, showEditRoleModal]);

  // Admin list query
  const adminsQuery = trpc.admin.list.useQuery({
    search: search || undefined,
    role: roleFilter || undefined,
    offset,
    limit,
  });

  // Invite mutation
  const inviteMutation = trpc.admin.invite.useMutation({
    onSuccess: () => {
      utils.admin.list.invalidate();
      // Move to success step in verification modal
      setVerificationStep(2);
    },
    onError: (error: { message: string }) => {
      setInviteError(error.message);
    },
  });

  // Remove mutation
  const removeMutation = trpc.admin.remove.useMutation({
    onSuccess: () => {
      utils.admin.list.invalidate();
    },
  });

  // Update role mutation
  const updateRoleMutation = trpc.admin.updateRole.useMutation({
    onSuccess: () => {
      utils.admin.list.invalidate();
      handleCloseEditRoleModal();
    },
  });

  // Current user query (for checking if user is super admin)
  const currentUserQuery = trpc.me.useQuery();

  // Integrations query and mutations
  const integrationsQuery = trpc.integrations.list.useQuery();

  const configureIntegrationMutation = trpc.integrations.configure.useMutation({
    onSuccess: () => {
      utils.integrations.list.invalidate();
      handleCloseIntegrationModal();
      setSettingsSaveMessage({ type: 'success', text: `${selectedIntegration?.name} connected successfully` });
      setTimeout(() => setSettingsSaveMessage(null), 3000);
    },
    onError: (error: { message: string }) => {
      setIntegrationError(error.message);
    },
  });

  const disableIntegrationMutation = trpc.integrations.disable.useMutation({
    onSuccess: () => {
      utils.integrations.list.invalidate();
      setSettingsSaveMessage({ type: 'success', text: 'Integration disconnected' });
      setTimeout(() => setSettingsSaveMessage(null), 3000);
    },
    onError: (error: { message: string }) => {
      setSettingsSaveMessage({ type: 'error', text: `Failed to disconnect: ${error.message}` });
    },
  });

  const validateIntegrationMutation = trpc.integrations.validate.useMutation();

  // Developer tools mutations
  const triggerDailyMessagesMutation = trpc.dev.triggerDailyMessages.useMutation({
    onSuccess: (data) => {
      setDevToolsMessage({ type: 'success', text: `Daily messages triggered (Job ID: ${data.jobId})` });
    },
    onError: (error: { message: string }) => {
      setDevToolsMessage({ type: 'error', text: `Failed to trigger daily messages: ${error.message}` });
    },
  });

  const triggerAllSubscriptionChecksMutation = trpc.dev.triggerAllSubscriptionChecks.useMutation({
    onSuccess: (data) => {
      setDevToolsMessage({
        type: 'success',
        text: `Subscription checks triggered (Trial: ${data.jobIds.trialExpiry}, Subscription: ${data.jobIds.subscriptionExpiry}, Churn: ${data.jobIds.churnRisk})`,
      });
    },
    onError: (error: { message: string }) => {
      setDevToolsMessage({ type: 'error', text: `Failed to trigger subscription checks: ${error.message}` });
    },
  });

  // Handle opening the invite modal
  const handleOpenInviteModal = () => {
    setShowInviteModal(true);
  };

  // Handle sending invite (opens verification modal)
  const handleSendInvite = () => {
    if (!inviteEmail.trim()) {
      setInviteError('Email is required');
      return;
    }
    setInviteError('');
    // Close invite modal and open verification modal
    setShowInviteModal(false);
    setShowVerificationModal(true);
    setVerificationStep(1);
  };

  // Handle verification code input
  const handleCodeInput = (index: number, value: string) => {
    if (value.length > 1) {
      // Handle paste
      const pastedCode = value.slice(0, 6).split('');
      const newCode = [...verificationCode];
      pastedCode.forEach((char, i) => {
        if (index + i < 6) {
          newCode[index + i] = char;
        }
      });
      setVerificationCode(newCode);
      // Focus last filled input or next empty
      const nextIndex = Math.min(index + pastedCode.length, 5);
      codeInputRefs.current[nextIndex]?.focus();
      return;
    }

    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);

    // Move to next input
    if (value && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };

  // Handle backspace in verification code
  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  // Handle verify and send
  const handleVerifyAndSend = () => {
    // In real implementation, this would verify the code
    // For now, we mock the verification and send the invite
    inviteMutation.mutate({
      email: inviteEmail.trim(),
      name: inviteName.trim() || undefined,
      role: inviteRole,
    });
  };

  // Handle done after successful invite
  const handleInviteDone = () => {
    handleCloseVerificationModal();
    setInviteEmail('');
    setInviteName('');
    setInviteRole('support');
  };

  const handleRemove = (adminId: string, adminEmail: string) => {
    if (confirm(`Are you sure you want to remove ${adminEmail} from the team?`)) {
      removeMutation.mutate({ id: adminId });
    }
  };

  // Handle opening edit role modal
  const handleOpenEditRoleModal = (admin: { id: string; name: string | null; email: string; role: string }) => {
    setEditRoleAdmin(admin);
    setEditRoleNewRole(admin.role as AdminRole);
    setEditRoleError('');
    setShowEditRoleModal(true);
  };

  // Handle updating role
  const handleUpdateRole = () => {
    if (!editRoleAdmin) return;

    setEditRoleError('');
    updateRoleMutation.mutate(
      { id: editRoleAdmin.id, role: editRoleNewRole },
      {
        onError: (error: { message: string }) => {
          setEditRoleError(error.message);
        },
      }
    );
  };

  // Check if current user is super admin
  const isSuperAdmin = currentUserQuery.data?.role === 'super_admin';

  // Handler for saving general settings
  const handleSaveGeneralSettings = () => {
    updateSettingsMutation.mutate({
      appName,
      autoModerationEnabled,
      alertThreshold,
      summaryModel: summaryModel || null,
    });
  };

  // Toggle auto-moderation handler
  const handleToggleAutoModeration = () => {
    const newValue = !autoModerationEnabled;
    setAutoModerationEnabled(newValue);
    // Auto-save when toggling
    updateSettingsMutation.mutate({
      autoModerationEnabled: newValue,
    });
  };

  // Toggle notification preference handler
  const handleToggleNotificationPref = (prefKey: 'safetyAlerts' | 'safetyAlertsHighOnly' | 'dailySummary' | 'supportTickets' | 'subscriptionAlerts') => {
    const currentValue = notificationPrefsQuery.data?.[prefKey] ?? true;
    updateNotificationPrefsMutation.mutate({
      [prefKey]: !currentValue,
    });
  };

  // Open integration config modal
  const handleOpenIntegrationModal = (integration: Integration) => {
    setSelectedIntegration(integration);
    setIntegrationApiKey('');
    setIntegrationWebhookUrl('');
    setIntegrationError('');
    setShowIntegrationModal(true);
  };

  // Handle integration connect/disconnect
  const handleToggleIntegration = (integration: Integration) => {
    if (integration.connected) {
      // Disconnect the integration
      disableIntegrationMutation.mutate({
        integrationType: integration.id,
      });
    } else {
      // Open modal to configure
      handleOpenIntegrationModal(integration);
    }
  };

  // Handle integration form submit
  const handleConfigureIntegration = async () => {
    if (!selectedIntegration) return;

    setIntegrationError('');
    setIntegrationValidating(true);

    try {
      // Validate first
      const validationResult = await validateIntegrationMutation.mutateAsync({
        integrationType: selectedIntegration.id,
        apiKey: selectedIntegration.requiresApiKey ? integrationApiKey : undefined,
        webhookUrl: selectedIntegration.requiresWebhookUrl ? integrationWebhookUrl : undefined,
      });

      if (!validationResult.valid) {
        setIntegrationError(validationResult.error || 'Validation failed');
        setIntegrationValidating(false);
        return;
      }

      // Configure the integration
      configureIntegrationMutation.mutate({
        integrationType: selectedIntegration.id,
        apiKey: selectedIntegration.requiresApiKey ? integrationApiKey : undefined,
        webhookUrl: selectedIntegration.requiresWebhookUrl ? integrationWebhookUrl : undefined,
      });
    } catch (error) {
      setIntegrationError(error instanceof Error ? error.message : 'Validation failed');
    } finally {
      setIntegrationValidating(false);
    }
  };

  // Navigation items for sidebar
  const navItems: { id: SettingsSection; label: string; icon: string }[] = [
    { id: 'general', label: 'General', icon: '⚙️' },
    { id: 'team', label: 'Team', icon: '👥' },
    { id: 'billing', label: 'Billing', icon: '💳' },
    { id: 'api', label: 'API', icon: '🔌' },
    { id: 'notifications', label: 'Notifications', icon: '🔔' },
    ...(staticSettings.environment === 'development'
      ? [{ id: 'developer' as SettingsSection, label: 'Developer', icon: '🛠️' }]
      : []),
  ];

  const renderContent = () => {
    switch (activeSection) {
      case 'general':
        return (
          <>
            <div style={styles.contentHeader}>
              <div style={styles.contentHeaderRow}>
                <div>
                  <h2 style={styles.contentTitle}>General Settings</h2>
                  <p style={styles.contentDescription}>
                    Configure basic application settings and preferences.
                  </p>
                </div>
                <button
                  onClick={handleSaveGeneralSettings}
                  disabled={updateSettingsMutation.isPending || settingsQuery.isLoading}
                  style={{
                    ...styles.saveButton,
                    ...(updateSettingsMutation.isPending ? styles.saveButtonDisabled : {}),
                  }}
                >
                  {updateSettingsMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>

            {settingsSaveMessage && (
              <div
                style={{
                  ...styles.settingsSaveMessage,
                  ...(settingsSaveMessage.type === 'success' ? styles.settingsSaveMessageSuccess : styles.settingsSaveMessageError),
                }}
              >
                {settingsSaveMessage.text}
                <button
                  onClick={() => setSettingsSaveMessage(null)}
                  style={styles.settingsSaveMessageClose}
                >
                  ×
                </button>
              </div>
            )}

            {settingsQuery.isLoading ? (
              <div style={styles.settingsCard}>
                <div style={styles.teamLoading}>Loading settings...</div>
              </div>
            ) : settingsQuery.error ? (
              <div style={styles.settingsCard}>
                <div style={styles.teamError}>
                  Error loading settings: {settingsQuery.error.message}
                </div>
              </div>
            ) : (
              <div style={styles.settingsCard}>
                <div style={styles.settingItem}>
                  <div style={styles.settingInfo}>
                    <span style={styles.settingLabel}>Application Name</span>
                    <span style={styles.settingDescription}>
                      The name displayed in the admin interface
                    </span>
                  </div>
                  <input
                    type="text"
                    value={appName}
                    onChange={(e) => setAppName(e.target.value)}
                    style={styles.settingInput}
                    placeholder="Enter application name"
                  />
                </div>

                <div style={styles.settingItem}>
                  <div style={styles.settingInfo}>
                    <span style={styles.settingLabel}>Environment</span>
                    <span style={styles.settingDescription}>
                      Current deployment environment
                    </span>
                  </div>
                  <span
                    style={{
                      ...styles.badge,
                      ...(staticSettings.environment === 'production'
                        ? styles.badgeProduction
                        : styles.badgeDevelopment),
                    }}
                  >
                    {staticSettings.environment}
                  </span>
                </div>

                <div style={styles.settingItem}>
                  <div style={styles.settingInfo}>
                    <span style={styles.settingLabel}>Auto-moderation</span>
                    <span style={styles.settingDescription}>
                      Automatically flag potentially harmful content
                    </span>
                  </div>
                  <button
                    onClick={handleToggleAutoModeration}
                    disabled={updateSettingsMutation.isPending}
                    style={{
                      ...styles.toggleButton,
                      ...(autoModerationEnabled ? styles.toggleButtonEnabled : styles.toggleButtonDisabled),
                    }}
                    aria-pressed={autoModerationEnabled}
                    aria-label={`Auto-moderation ${autoModerationEnabled ? 'enabled' : 'disabled'}`}
                  >
                    <span
                      style={{
                        ...styles.toggleKnob,
                        ...(autoModerationEnabled ? styles.toggleKnobEnabled : styles.toggleKnobDisabled),
                      }}
                    />
                    <span style={styles.toggleLabel}>
                      {autoModerationEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </button>
                </div>

                <div style={styles.settingItem}>
                  <div style={styles.settingInfo}>
                    <span style={styles.settingLabel}>Alert Threshold</span>
                    <span style={styles.settingDescription}>
                      Minimum severity level for safety alerts
                    </span>
                  </div>
                  <select
                    value={alertThreshold}
                    onChange={(e) => setAlertThreshold(e.target.value as AlertThreshold)}
                    style={styles.settingSelect}
                  >
                    <option value="low">Low - Flag all concerns</option>
                    <option value="medium">Medium - Flag moderate and high</option>
                    <option value="high">High - Only flag critical concerns</option>
                  </select>
                </div>

                <div style={{ ...styles.settingItem, borderBottom: 'none' }}>
                  <div style={styles.settingInfo}>
                    <span style={styles.settingLabel}>Summary AI Model</span>
                    <span style={styles.settingDescription}>
                      Default AI model for generating daily/weekly summaries (can be overridden per bot)
                    </span>
                  </div>
                  <input
                    type="text"
                    value={summaryModel}
                    onChange={(e) => setSummaryModel(e.target.value)}
                    style={styles.settingInput}
                    placeholder="e.g., openai/gpt-4o-mini"
                  />
                </div>
              </div>
            )}

            {/* Integrations Section */}
            <div style={styles.sectionHeader}>
              <h3 style={styles.sectionTitle}>Integrations</h3>
              <p style={styles.sectionDescription}>
                Connect external services to enhance your workflow.
              </p>
            </div>

            {integrationsQuery.isLoading ? (
              <div style={styles.settingsCard}>
                <div style={styles.teamLoading}>Loading integrations...</div>
              </div>
            ) : integrationsQuery.error ? (
              <div style={styles.settingsCard}>
                <div style={styles.teamError}>
                  Error loading integrations: {integrationsQuery.error.message}
                </div>
              </div>
            ) : (
              <div style={styles.integrationsGrid}>
                {integrationsQuery.data?.map((integration) => (
                  <div key={integration.id} style={styles.integrationCard}>
                    <div style={styles.integrationHeader}>
                      <span style={styles.integrationIcon}>{integration.icon}</span>
                      <div style={styles.integrationInfo}>
                        <span style={styles.integrationName}>{integration.name}</span>
                        <span style={styles.integrationDescription}>{integration.description}</span>
                      </div>
                    </div>
                    {integration.connected && integration.verifiedAt && (
                      <div style={styles.integrationMeta}>
                        <span style={styles.integrationMetaText}>
                          Connected {new Date(integration.verifiedAt).toLocaleDateString()}
                        </span>
                        {integration.hasApiKey && integration.maskedApiKey && (
                          <span style={styles.integrationMetaText}>
                            API Key: {integration.maskedApiKey}
                          </span>
                        )}
                        {integration.hasWebhookUrl && integration.maskedWebhookUrl && (
                          <span style={styles.integrationMetaText}>
                            Webhook: {integration.maskedWebhookUrl}
                          </span>
                        )}
                      </div>
                    )}
                    <button
                      onClick={() => handleToggleIntegration(integration as Integration)}
                      disabled={disableIntegrationMutation.isPending}
                      style={{
                        ...styles.integrationButton,
                        ...(integration.connected ? styles.integrationButtonConnected : styles.integrationButtonDisconnected),
                      }}
                    >
                      {integration.connected ? (
                        <>
                          <span style={styles.integrationStatusDot} />
                          {disableIntegrationMutation.isPending ? 'Disconnecting...' : 'Connected'}
                        </>
                      ) : (
                        'Connect'
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        );

      case 'team':
        return (
          <>
            <div style={styles.contentHeader}>
              <div style={styles.teamHeaderRow}>
                <div>
                  <h2 style={styles.contentTitle}>Team Management</h2>
                  <p style={styles.contentDescription}>
                    Manage team members and their access permissions.
                    {adminsQuery.data && (
                      <span style={styles.teamCount}>
                        {' '}{adminsQuery.data.pagination.total} member{adminsQuery.data.pagination.total !== 1 ? 's' : ''}
                      </span>
                    )}
                  </p>
                </div>
                <button
                  onClick={handleOpenInviteModal}
                  style={styles.inviteButton}
                >
                  + Invite Admin
                </button>
              </div>
            </div>

            {/* Team Filters */}
            <div style={styles.teamFilters}>
              <input
                type="text"
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setOffset(0);
                }}
                style={styles.searchInput}
              />
              <select
                value={roleFilter}
                onChange={(e) => {
                  setRoleFilter(e.target.value as AdminRole | '');
                  setOffset(0);
                }}
                style={styles.filterSelect}
              >
                <option value="">All Roles</option>
                <option value="super_admin">Super Admin</option>
                <option value="support">Support</option>
                <option value="marketing">Marketing</option>
              </select>
            </div>

            {/* Team List */}
            <div style={styles.settingsCard}>
              {adminsQuery.isLoading ? (
                <div style={styles.teamLoading}>Loading team members...</div>
              ) : adminsQuery.error ? (
                <div style={styles.teamError}>
                  Error loading team: {adminsQuery.error.message}
                </div>
              ) : adminsQuery.data?.admins.length === 0 ? (
                <div style={styles.emptyState}>
                  <div style={styles.emptyIcon}>👥</div>
                  <div style={styles.emptyText}>No team members found</div>
                  {(search || roleFilter) && (
                    <button
                      onClick={() => {
                        setSearch('');
                        setRoleFilter('');
                      }}
                      style={styles.clearFiltersButton}
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div style={styles.teamList}>
                    {adminsQuery.data?.admins.map((admin) => (
                      <div key={admin.id} style={styles.teamMember}>
                        <div style={styles.memberInfo}>
                          <div style={{
                            ...styles.memberAvatar,
                            ...getAvatarStyle(admin.role),
                          }}>
                            {admin.name?.[0]?.toUpperCase() || admin.email[0].toUpperCase()}
                          </div>
                          <div style={styles.memberDetails}>
                            <div style={styles.memberName}>
                              {admin.name || 'Unnamed Admin'}
                            </div>
                            <div style={styles.memberEmail}>{admin.email}</div>
                          </div>
                        </div>
                        <div style={styles.memberMeta}>
                          <div style={styles.memberRoleContainer}>
                            {isSuperAdmin && currentUserQuery.data?.id !== admin.id ? (
                              <button
                                onClick={() => handleOpenEditRoleModal(admin)}
                                style={{
                                  ...styles.roleBadgeButton,
                                  ...getRoleStyle(admin.role),
                                }}
                                title="Click to edit role"
                              >
                                {formatRole(admin.role)}
                                <span style={styles.editRoleIcon}>✎</span>
                              </button>
                            ) : (
                              <span
                                style={{
                                  ...styles.roleBadge,
                                  ...getRoleStyle(admin.role),
                                }}
                              >
                                {formatRole(admin.role)}
                              </span>
                            )}
                            <span style={styles.memberStatus}>
                              Active {admin.createdAt ? formatDate(admin.createdAt) : 'Unknown'}
                            </span>
                          </div>
                          <button
                            onClick={() => handleRemove(admin.id, admin.email)}
                            disabled={removeMutation.isPending}
                            style={styles.removeButton}
                            title="Remove admin"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Security Note */}
                  <div style={styles.securityNote}>
                    <span style={styles.securityNoteIcon}>🔒</span>
                    <div>
                      <p style={styles.securityNoteTitle}>
                        Two-Step Verification Required
                      </p>
                      <p style={styles.securityNoteText}>
                        Adding new team members requires email verification from both the inviter and a confirmation code sent to the invited member. This ensures secure access to sensitive user data.
                      </p>
                    </div>
                  </div>

                  {/* Pagination */}
                  {adminsQuery.data && adminsQuery.data.pagination.total > limit && (
                    <div style={styles.pagination}>
                      <button
                        onClick={() => setOffset((o) => Math.max(0, o - limit))}
                        disabled={offset === 0}
                        style={{
                          ...styles.pageButton,
                          ...(offset === 0 ? styles.pageButtonDisabled : {}),
                        }}
                      >
                        Previous
                      </button>
                      <span style={styles.pageInfo}>
                        {offset + 1} - {Math.min(offset + limit, adminsQuery.data.pagination.total)} of {adminsQuery.data.pagination.total}
                      </span>
                      <button
                        onClick={() => setOffset((o) => o + limit)}
                        disabled={!adminsQuery.data.pagination.hasMore}
                        style={{
                          ...styles.pageButton,
                          ...(!adminsQuery.data.pagination.hasMore ? styles.pageButtonDisabled : {}),
                        }}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {removeMutation.error && (
              <div style={styles.teamError}>
                Error removing admin: {removeMutation.error.message}
              </div>
            )}
          </>
        );

      case 'billing':
        return (
          <>
            <div style={styles.contentHeader}>
              <h2 style={styles.contentTitle}>Billing</h2>
              <p style={styles.contentDescription}>
                Manage your subscription and billing information.
              </p>
            </div>

            <div style={styles.settingsCard}>
              <div style={styles.emptyState}>
                <div style={styles.emptyIcon}>💳</div>
                <div style={styles.emptyText}>Billing settings coming soon</div>
              </div>
            </div>
          </>
        );

      case 'api':
        return (
          <>
            <div style={styles.contentHeader}>
              <h2 style={styles.contentTitle}>API Configuration</h2>
              <p style={styles.contentDescription}>
                Manage API settings and credentials.
              </p>
            </div>

            <div style={styles.settingsCard}>
              <div style={styles.settingItem}>
                <div style={styles.settingInfo}>
                  <span style={styles.settingLabel}>API URL</span>
                  <span style={styles.settingDescription}>
                    Base URL for API requests
                  </span>
                </div>
                <code style={styles.codeValue}>{staticSettings.apiUrl}</code>
              </div>

              <div style={{ ...styles.settingItem, borderBottom: 'none' }}>
                <div style={styles.settingInfo}>
                  <span style={styles.settingLabel}>API Version</span>
                  <span style={styles.settingDescription}>
                    Current API version in use
                  </span>
                </div>
                <span style={styles.settingValue}>{staticSettings.apiVersion}</span>
              </div>
            </div>
          </>
        );

      case 'notifications':
        return (
          <>
            <div style={styles.contentHeader}>
              <h2 style={styles.contentTitle}>Notifications</h2>
              <p style={styles.contentDescription}>
                Configure your personal notification preferences. These settings control which email notifications you receive.
              </p>
            </div>

            {notificationPrefsMessage && (
              <div
                style={{
                  ...styles.settingsSaveMessage,
                  ...(notificationPrefsMessage.type === 'success' ? styles.settingsSaveMessageSuccess : styles.settingsSaveMessageError),
                }}
              >
                {notificationPrefsMessage.text}
                <button
                  onClick={() => setNotificationPrefsMessage(null)}
                  style={styles.settingsSaveMessageClose}
                >
                  ×
                </button>
              </div>
            )}

            {/* System-wide settings info */}
            <div style={styles.settingsCard}>
              <div style={styles.settingItem}>
                <div style={styles.settingInfo}>
                  <span style={styles.settingLabel}>System Email Notifications</span>
                  <span style={styles.settingDescription}>
                    Global email notification status (controlled by super admin)
                  </span>
                </div>
                <span
                  style={{
                    ...styles.badge,
                    ...(settingsQuery.data?.emailNotificationsEnabled ? styles.badgeEnabled : styles.badgeDisabled),
                  }}
                >
                  {settingsQuery.data?.emailNotificationsEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>

              <div style={{ ...styles.settingItem, borderBottom: 'none' }}>
                <div style={styles.settingInfo}>
                  <span style={styles.settingLabel}>System Notification Email</span>
                  <span style={styles.settingDescription}>
                    Default email address for system-wide notifications
                  </span>
                </div>
                <span style={styles.settingValue}>{settingsQuery.data?.notificationEmail || 'Not configured'}</span>
              </div>
            </div>

            {/* Personal notification preferences */}
            <div style={styles.sectionHeader}>
              <h3 style={styles.sectionTitle}>Your Notification Preferences</h3>
              <p style={styles.sectionDescription}>
                Choose which types of email notifications you want to receive.
              </p>
            </div>

            {notificationPrefsQuery.isLoading ? (
              <div style={styles.settingsCard}>
                <div style={styles.teamLoading}>Loading preferences...</div>
              </div>
            ) : notificationPrefsQuery.error ? (
              <div style={styles.settingsCard}>
                <div style={styles.teamError}>
                  Error loading preferences: {notificationPrefsQuery.error.message}
                </div>
              </div>
            ) : (
              <div style={styles.settingsCard}>
                <div style={styles.settingItem}>
                  <div style={styles.settingInfo}>
                    <span style={styles.settingLabel}>Safety Alerts</span>
                    <span style={styles.settingDescription}>
                      Receive email notifications when safety alerts are triggered
                    </span>
                  </div>
                  <button
                    onClick={() => handleToggleNotificationPref('safetyAlerts')}
                    disabled={updateNotificationPrefsMutation.isPending}
                    style={{
                      ...styles.toggleButton,
                      ...(notificationPrefsQuery.data?.safetyAlerts ? styles.toggleButtonEnabled : styles.toggleButtonDisabled),
                    }}
                    aria-pressed={notificationPrefsQuery.data?.safetyAlerts}
                    aria-label={`Safety alerts ${notificationPrefsQuery.data?.safetyAlerts ? 'enabled' : 'disabled'}`}
                  >
                    <span
                      style={{
                        ...styles.toggleKnob,
                        ...(notificationPrefsQuery.data?.safetyAlerts ? styles.toggleKnobEnabled : styles.toggleKnobDisabled),
                      }}
                    />
                    <span style={styles.toggleLabel}>
                      {notificationPrefsQuery.data?.safetyAlerts ? 'Enabled' : 'Disabled'}
                    </span>
                  </button>
                </div>

                {notificationPrefsQuery.data?.safetyAlerts && (
                  <div style={{ ...styles.settingItem, paddingLeft: '24px', backgroundColor: '#f8fafc' }}>
                    <div style={styles.settingInfo}>
                      <span style={styles.settingLabel}>High Severity Only</span>
                      <span style={styles.settingDescription}>
                        Only receive safety alerts for high severity issues
                      </span>
                    </div>
                    <button
                      onClick={() => handleToggleNotificationPref('safetyAlertsHighOnly')}
                      disabled={updateNotificationPrefsMutation.isPending}
                      style={{
                        ...styles.toggleButton,
                        ...(notificationPrefsQuery.data?.safetyAlertsHighOnly ? styles.toggleButtonEnabled : styles.toggleButtonDisabled),
                      }}
                      aria-pressed={notificationPrefsQuery.data?.safetyAlertsHighOnly}
                      aria-label={`High severity only ${notificationPrefsQuery.data?.safetyAlertsHighOnly ? 'enabled' : 'disabled'}`}
                    >
                      <span
                        style={{
                          ...styles.toggleKnob,
                          ...(notificationPrefsQuery.data?.safetyAlertsHighOnly ? styles.toggleKnobEnabled : styles.toggleKnobDisabled),
                        }}
                      />
                      <span style={styles.toggleLabel}>
                        {notificationPrefsQuery.data?.safetyAlertsHighOnly ? 'Enabled' : 'Disabled'}
                      </span>
                    </button>
                  </div>
                )}

                <div style={styles.settingItem}>
                  <div style={styles.settingInfo}>
                    <span style={styles.settingLabel}>Daily Summary</span>
                    <span style={styles.settingDescription}>
                      Receive a daily summary of activity and metrics
                    </span>
                  </div>
                  <button
                    onClick={() => handleToggleNotificationPref('dailySummary')}
                    disabled={updateNotificationPrefsMutation.isPending}
                    style={{
                      ...styles.toggleButton,
                      ...(notificationPrefsQuery.data?.dailySummary ? styles.toggleButtonEnabled : styles.toggleButtonDisabled),
                    }}
                    aria-pressed={notificationPrefsQuery.data?.dailySummary}
                    aria-label={`Daily summary ${notificationPrefsQuery.data?.dailySummary ? 'enabled' : 'disabled'}`}
                  >
                    <span
                      style={{
                        ...styles.toggleKnob,
                        ...(notificationPrefsQuery.data?.dailySummary ? styles.toggleKnobEnabled : styles.toggleKnobDisabled),
                      }}
                    />
                    <span style={styles.toggleLabel}>
                      {notificationPrefsQuery.data?.dailySummary ? 'Enabled' : 'Disabled'}
                    </span>
                  </button>
                </div>

                <div style={styles.settingItem}>
                  <div style={styles.settingInfo}>
                    <span style={styles.settingLabel}>Support Tickets</span>
                    <span style={styles.settingDescription}>
                      Receive notifications when new support tickets are created
                    </span>
                  </div>
                  <button
                    onClick={() => handleToggleNotificationPref('supportTickets')}
                    disabled={updateNotificationPrefsMutation.isPending}
                    style={{
                      ...styles.toggleButton,
                      ...(notificationPrefsQuery.data?.supportTickets ? styles.toggleButtonEnabled : styles.toggleButtonDisabled),
                    }}
                    aria-pressed={notificationPrefsQuery.data?.supportTickets}
                    aria-label={`Support tickets ${notificationPrefsQuery.data?.supportTickets ? 'enabled' : 'disabled'}`}
                  >
                    <span
                      style={{
                        ...styles.toggleKnob,
                        ...(notificationPrefsQuery.data?.supportTickets ? styles.toggleKnobEnabled : styles.toggleKnobDisabled),
                      }}
                    />
                    <span style={styles.toggleLabel}>
                      {notificationPrefsQuery.data?.supportTickets ? 'Enabled' : 'Disabled'}
                    </span>
                  </button>
                </div>

                <div style={{ ...styles.settingItem, borderBottom: 'none' }}>
                  <div style={styles.settingInfo}>
                    <span style={styles.settingLabel}>Subscription Alerts</span>
                    <span style={styles.settingDescription}>
                      Receive notifications for subscription events (new, expired, etc.)
                    </span>
                  </div>
                  <button
                    onClick={() => handleToggleNotificationPref('subscriptionAlerts')}
                    disabled={updateNotificationPrefsMutation.isPending}
                    style={{
                      ...styles.toggleButton,
                      ...(notificationPrefsQuery.data?.subscriptionAlerts ? styles.toggleButtonEnabled : styles.toggleButtonDisabled),
                    }}
                    aria-pressed={notificationPrefsQuery.data?.subscriptionAlerts}
                    aria-label={`Subscription alerts ${notificationPrefsQuery.data?.subscriptionAlerts ? 'enabled' : 'disabled'}`}
                  >
                    <span
                      style={{
                        ...styles.toggleKnob,
                        ...(notificationPrefsQuery.data?.subscriptionAlerts ? styles.toggleKnobEnabled : styles.toggleKnobDisabled),
                      }}
                    />
                    <span style={styles.toggleLabel}>
                      {notificationPrefsQuery.data?.subscriptionAlerts ? 'Enabled' : 'Disabled'}
                    </span>
                  </button>
                </div>
              </div>
            )}
          </>
        );

      case 'developer':
        return (
          <>
            <div style={styles.contentHeader}>
              <h2 style={styles.contentTitle}>Developer Tools</h2>
              <p style={styles.contentDescription}>
                Manually trigger scheduled jobs for testing. These tools are only available in development mode.
              </p>
            </div>

            {devToolsMessage && (
              <div
                style={{
                  ...styles.devToolsMessage,
                  ...(devToolsMessage.type === 'success' ? styles.devToolsMessageSuccess : styles.devToolsMessageError),
                }}
              >
                {devToolsMessage.text}
                <button
                  onClick={() => setDevToolsMessage(null)}
                  style={styles.devToolsMessageClose}
                >
                  ×
                </button>
              </div>
            )}

            <div style={styles.devToolsGrid}>
              <div style={styles.devToolCard}>
                <div style={styles.devToolCardHeader}>
                  <span style={styles.devToolCardIcon}>📨</span>
                  <span style={styles.devToolCardTitle}>Daily Messages</span>
                </div>
                <p style={styles.devToolCardDescription}>
                  Trigger the daily message scheduler to process users whose preferred time matches now.
                </p>
                <button
                  onClick={() => triggerDailyMessagesMutation.mutate()}
                  disabled={triggerDailyMessagesMutation.isPending}
                  style={{
                    ...styles.devToolButton,
                    ...(triggerDailyMessagesMutation.isPending ? styles.devToolButtonDisabled : {}),
                  }}
                >
                  {triggerDailyMessagesMutation.isPending ? 'Triggering...' : 'Trigger Daily Messages'}
                </button>
              </div>

              <div style={styles.devToolCard}>
                <div style={styles.devToolCardHeader}>
                  <span style={styles.devToolCardIcon}>📋</span>
                  <span style={styles.devToolCardTitle}>Subscription Checks</span>
                </div>
                <p style={styles.devToolCardDescription}>
                  Trigger all subscription checks: trial expiry, subscription expiry, and churn risk calculation.
                </p>
                <button
                  onClick={() => triggerAllSubscriptionChecksMutation.mutate()}
                  disabled={triggerAllSubscriptionChecksMutation.isPending}
                  style={{
                    ...styles.devToolButton,
                    ...(triggerAllSubscriptionChecksMutation.isPending ? styles.devToolButtonDisabled : {}),
                  }}
                >
                  {triggerAllSubscriptionChecksMutation.isPending ? 'Triggering...' : 'Trigger Subscription Checks'}
                </button>
              </div>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div style={styles.container}>
      {/* Sidebar Navigation */}
      <div style={styles.sidebar}>
        <div style={styles.sidebarHeader}>
          <h1 style={styles.sidebarTitle}>Settings</h1>
        </div>
        <nav style={styles.nav}>
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveSection(item.id)}
              style={{
                ...styles.navItem,
                ...(activeSection === item.id ? styles.navItemActive : {}),
              }}
            >
              <span style={styles.navIcon}>{item.icon}</span>
              <span style={styles.navLabel}>{item.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Content Area */}
      <div style={styles.content}>
        {renderContent()}
      </div>

      {/* Invite Admin Modal */}
      {showInviteModal && (
        <div style={styles.modalOverlay}>
          <div ref={inviteModalRef} style={styles.inviteModalContainer}>
            {/* Modal Header */}
            <div style={styles.modalHeader}>
              <div>
                <h2 style={styles.modalTitle}>Add Team Member</h2>
                <p style={styles.modalSubtitle}>Invite a new admin to your team</p>
              </div>
              <button
                onClick={handleCloseInviteModal}
                style={styles.modalCloseButton}
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div style={styles.modalBody}>
              <div style={styles.modalFormGroup}>
                <label style={styles.modalFormLabel}>Full Name</label>
                <input
                  type="text"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  placeholder="Enter full name"
                  style={styles.modalFormInput}
                />
              </div>

              <div style={styles.modalFormGroup}>
                <label style={styles.modalFormLabel}>Email Address</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="Enter email address"
                  style={styles.modalFormInput}
                />
              </div>

              <div style={styles.modalFormGroup}>
                <label style={styles.modalFormLabel}>Role</label>
                <div style={styles.roleOptionsContainer}>
                  {(Object.entries(roleInfo) as [AdminRole, typeof roleInfo[AdminRole]][]).map(([role, info]) => (
                    <label
                      key={role}
                      style={{
                        ...styles.roleOption,
                        ...(inviteRole === role ? styles.roleOptionSelected : {}),
                      }}
                    >
                      <input
                        type="radio"
                        name="role"
                        value={role}
                        checked={inviteRole === role}
                        onChange={() => setInviteRole(role)}
                        style={styles.roleRadio}
                      />
                      <div style={styles.roleOptionContent}>
                        <div style={styles.roleOptionHeader}>
                          <span
                            style={{
                              ...styles.roleOptionBadge,
                              backgroundColor: info.bg,
                              color: info.color,
                            }}
                          >
                            {info.label}
                          </span>
                        </div>
                        <p style={styles.roleOptionDescription}>{info.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {inviteError && (
                <div style={styles.formError}>{inviteError}</div>
              )}

              {/* Security Notice */}
              <div style={styles.securityNotice}>
                <div style={styles.securityNoticeContent}>
                  <span style={styles.securityNoticeIcon}>🔐</span>
                  <div>
                    <p style={styles.securityNoticeTitle}>Double Verification Required</p>
                    <p style={styles.securityNoticeText}>
                      After clicking "Send Invite", you'll receive a verification code to your email. Enter it to confirm the invitation.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={styles.modalFooter}>
              <button
                onClick={handleCloseInviteModal}
                style={styles.modalCancelButton}
              >
                Cancel
              </button>
              <button
                onClick={handleSendInvite}
                style={styles.modalSubmitButton}
              >
                Send Invite →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Verification Modal */}
      {showVerificationModal && (
        <div style={styles.modalOverlay}>
          <div ref={verificationModalRef} style={styles.verificationModalContainer}>
            {/* Modal Header */}
            <div style={styles.verificationModalHeader}>
              <div
                style={{
                  ...styles.verificationIconCircle,
                  backgroundColor: verificationStep === 1 ? 'rgba(232, 200, 140, 0.15)' : 'rgba(152, 184, 154, 0.15)',
                }}
              >
                {verificationStep === 1 ? '🔐' : '✅'}
              </div>
              <h2 style={styles.modalTitle}>
                {verificationStep === 1 ? 'Verify Your Identity' : 'Invitation Sent!'}
              </h2>
              <p style={styles.modalSubtitle}>
                {verificationStep === 1
                  ? `We've sent a 6-digit code to your email`
                  : 'The team member will receive an invite email'}
              </p>
            </div>

            {/* Modal Body */}
            <div style={styles.modalBody}>
              {verificationStep === 1 ? (
                <>
                  <div style={styles.verificationCodeSection}>
                    <label style={styles.verificationCodeLabel}>Enter verification code</label>
                    <div style={styles.verificationCodeInputs}>
                      {[0, 1, 2, 3, 4, 5].map((index) => (
                        <input
                          key={index}
                          ref={(el) => { codeInputRefs.current[index] = el; }}
                          type="text"
                          maxLength={6}
                          value={verificationCode[index]}
                          onChange={(e) => handleCodeInput(index, e.target.value)}
                          onKeyDown={(e) => handleCodeKeyDown(index, e)}
                          style={styles.verificationCodeInput}
                        />
                      ))}
                    </div>
                  </div>

                  <p style={styles.resendCodeText}>
                    Didn't receive the code?{' '}
                    <button style={styles.resendCodeButton}>Resend</button>
                  </p>

                  <button
                    onClick={handleVerifyAndSend}
                    disabled={inviteMutation.isPending}
                    style={{
                      ...styles.verifyButton,
                      ...(inviteMutation.isPending ? styles.verifyButtonDisabled : {}),
                    }}
                  >
                    {inviteMutation.isPending ? 'Verifying...' : 'Verify & Send Invitation'}
                  </button>
                </>
              ) : (
                <>
                  <div style={styles.successCard}>
                    <div style={styles.successCardContent}>
                      <div style={styles.successAvatar}>
                        {inviteName ? inviteName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : inviteEmail[0].toUpperCase()}
                      </div>
                      <div style={styles.successInfo}>
                        <p style={styles.successName}>{inviteName || 'New Admin'}</p>
                        <p style={styles.successEmail}>{inviteEmail}</p>
                      </div>
                      <span
                        style={{
                          ...styles.successRoleBadge,
                          backgroundColor: roleInfo[inviteRole].bg,
                          color: roleInfo[inviteRole].color,
                        }}
                      >
                        {roleInfo[inviteRole].label}
                      </span>
                    </div>
                  </div>

                  <p style={styles.successInstructions}>
                    They'll need to click the link in their email to activate their account and set a password.
                  </p>

                  <button
                    onClick={handleInviteDone}
                    style={styles.doneButton}
                  >
                    Done
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Integration Configuration Modal */}
      {showIntegrationModal && selectedIntegration && (
        <div style={styles.modalOverlay}>
          <div ref={integrationModalRef} style={styles.inviteModalContainer}>
            {/* Modal Header */}
            <div style={styles.modalHeader}>
              <div>
                <h2 style={styles.modalTitle}>Connect {selectedIntegration.name}</h2>
                <p style={styles.modalSubtitle}>Configure your {selectedIntegration.name} integration</p>
              </div>
              <button
                onClick={handleCloseIntegrationModal}
                style={styles.modalCloseButton}
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div style={styles.modalBody}>
              <div style={styles.integrationModalIcon}>
                <span style={styles.integrationModalIconEmoji}>{selectedIntegration.icon}</span>
              </div>

              <p style={styles.integrationModalDescription}>{selectedIntegration.description}</p>

              {selectedIntegration.requiresApiKey && (
                <div style={styles.modalFormGroup}>
                  <label style={styles.modalFormLabel}>API Key</label>
                  <input
                    type="password"
                    value={integrationApiKey}
                    onChange={(e) => setIntegrationApiKey(e.target.value)}
                    placeholder={selectedIntegration.apiKeyPlaceholder || 'Enter API key'}
                    style={styles.modalFormInput}
                    autoComplete="off"
                  />
                </div>
              )}

              {selectedIntegration.requiresWebhookUrl && (
                <div style={styles.modalFormGroup}>
                  <label style={styles.modalFormLabel}>Webhook URL</label>
                  <input
                    type="url"
                    value={integrationWebhookUrl}
                    onChange={(e) => setIntegrationWebhookUrl(e.target.value)}
                    placeholder={selectedIntegration.webhookUrlPlaceholder || 'Enter webhook URL'}
                    style={styles.modalFormInput}
                    autoComplete="off"
                  />
                </div>
              )}

              {integrationError && (
                <div style={styles.formError}>{integrationError}</div>
              )}

              {selectedIntegration.helpUrl && (
                <div style={styles.integrationHelpLink}>
                  <a
                    href={selectedIntegration.helpUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.integrationHelpLinkAnchor}
                  >
                    Need help? View documentation →
                  </a>
                </div>
              )}

              {/* Security Notice */}
              <div style={styles.securityNotice}>
                <div style={styles.securityNoticeContent}>
                  <span style={styles.securityNoticeIcon}>🔐</span>
                  <div>
                    <p style={styles.securityNoticeTitle}>Secure Connection</p>
                    <p style={styles.securityNoticeText}>
                      Your credentials will be validated before saving. They are stored securely and never exposed in logs.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={styles.modalFooter}>
              <button
                onClick={handleCloseIntegrationModal}
                style={styles.modalCancelButton}
              >
                Cancel
              </button>
              <button
                onClick={handleConfigureIntegration}
                disabled={
                  integrationValidating ||
                  configureIntegrationMutation.isPending ||
                  (selectedIntegration.requiresApiKey && !integrationApiKey) ||
                  (selectedIntegration.requiresWebhookUrl && !integrationWebhookUrl)
                }
                style={{
                  ...styles.modalSubmitButton,
                  ...((integrationValidating || configureIntegrationMutation.isPending) ? styles.submitButtonDisabled : {}),
                }}
              >
                {integrationValidating ? 'Validating...' : configureIntegrationMutation.isPending ? 'Connecting...' : 'Connect'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Role Modal */}
      {showEditRoleModal && editRoleAdmin && (
        <div style={styles.modalOverlay}>
          <div ref={editRoleModalRef} style={styles.inviteModalContainer}>
            {/* Modal Header */}
            <div style={styles.modalHeader}>
              <div>
                <h2 style={styles.modalTitle}>Edit Role</h2>
                <p style={styles.modalSubtitle}>Change role for {editRoleAdmin.name || editRoleAdmin.email}</p>
              </div>
              <button
                onClick={handleCloseEditRoleModal}
                style={styles.modalCloseButton}
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div style={styles.modalBody}>
              <div style={styles.editRoleCurrentInfo}>
                <div style={{
                  ...styles.memberAvatar,
                  ...getAvatarStyle(editRoleAdmin.role),
                }}>
                  {editRoleAdmin.name?.[0]?.toUpperCase() || editRoleAdmin.email[0].toUpperCase()}
                </div>
                <div style={styles.editRoleCurrentDetails}>
                  <p style={styles.editRoleCurrentName}>{editRoleAdmin.name || 'Unnamed Admin'}</p>
                  <p style={styles.editRoleCurrentEmail}>{editRoleAdmin.email}</p>
                </div>
              </div>

              <div style={styles.modalFormGroup}>
                <label style={styles.modalFormLabel}>Select New Role</label>
                <div style={styles.roleOptionsContainer}>
                  {(Object.entries(roleInfo) as [AdminRole, typeof roleInfo[AdminRole]][]).map(([role, info]) => (
                    <label
                      key={role}
                      style={{
                        ...styles.roleOption,
                        ...(editRoleNewRole === role ? styles.roleOptionSelected : {}),
                      }}
                    >
                      <input
                        type="radio"
                        name="editRole"
                        value={role}
                        checked={editRoleNewRole === role}
                        onChange={() => setEditRoleNewRole(role)}
                        style={styles.roleRadio}
                      />
                      <div style={styles.roleOptionContent}>
                        <div style={styles.roleOptionHeader}>
                          <span
                            style={{
                              ...styles.roleOptionBadge,
                              backgroundColor: info.bg,
                              color: info.color,
                            }}
                          >
                            {info.label}
                          </span>
                          {editRoleAdmin.role === role && (
                            <span style={styles.currentRoleIndicator}>Current</span>
                          )}
                        </div>
                        <p style={styles.roleOptionDescription}>{info.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {editRoleError && (
                <div style={styles.formError}>{editRoleError}</div>
              )}

              {updateRoleMutation.error && !editRoleError && (
                <div style={styles.formError}>{updateRoleMutation.error.message}</div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={styles.modalFooter}>
              <button
                onClick={handleCloseEditRoleModal}
                style={styles.modalCancelButton}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateRole}
                disabled={updateRoleMutation.isPending || editRoleNewRole === editRoleAdmin.role}
                style={{
                  ...styles.modalSubmitButton,
                  ...((updateRoleMutation.isPending || editRoleNewRole === editRoleAdmin.role) ? styles.submitButtonDisabled : {}),
                }}
              >
                {updateRoleMutation.isPending ? 'Updating...' : 'Update Role'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function formatRole(role: string): string {
  switch (role) {
    case 'super_admin':
      return 'Super Admin';
    case 'support':
      return 'Support';
    case 'marketing':
      return 'Marketing';
    default:
      return role;
  }
}

function getRoleStyle(role: string): React.CSSProperties {
  switch (role) {
    case 'super_admin':
      return { backgroundColor: 'rgba(90, 106, 138, 0.15)', color: '#5A6A8A' };
    case 'support':
      return { backgroundColor: 'rgba(90, 138, 94, 0.15)', color: '#5A8A5E' };
    case 'marketing':
      return { backgroundColor: 'rgba(232, 200, 140, 0.15)', color: '#9B7B3C' };
    default:
      return { backgroundColor: '#f1f5f9', color: '#475569' };
  }
}

function getAvatarStyle(role: string): React.CSSProperties {
  switch (role) {
    case 'super_admin':
      return { background: 'linear-gradient(135deg, #5A6A8A 0%, rgba(90, 106, 138, 0.5) 100%)' };
    case 'support':
      return { background: 'linear-gradient(135deg, #5A8A5E 0%, rgba(90, 138, 94, 0.5) 100%)' };
    case 'marketing':
      return { background: 'linear-gradient(135deg, #9B7B3C 0%, rgba(155, 123, 60, 0.5) 100%)' };
    default:
      return { background: '#e2e8f0' };
  }
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    gap: '32px',
    minHeight: '100%',
  },
  // Sidebar styles
  sidebar: {
    width: '220px',
    flexShrink: 0,
  },
  sidebarHeader: {
    marginBottom: '24px',
  },
  sidebarTitle: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#1e293b',
    margin: 0,
  },
  nav: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    width: '100%',
    padding: '12px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    textAlign: 'left',
    fontSize: '14px',
    fontWeight: '500',
    color: '#64748b',
    transition: 'all 0.15s ease',
  },
  navItemActive: {
    backgroundColor: '#f1f5f9',
    color: '#1e293b',
  },
  navIcon: {
    fontSize: '16px',
    width: '20px',
    textAlign: 'center',
  },
  navLabel: {
    flex: 1,
  },
  // Content area styles
  content: {
    flex: 1,
    minWidth: 0,
  },
  contentHeader: {
    marginBottom: '24px',
  },
  contentTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1e293b',
    margin: '0 0 8px 0',
  },
  contentDescription: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0,
  },
  // Settings card styles
  settingsCard: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    marginBottom: '24px',
  },
  settingItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 0',
    borderBottom: '1px solid #f1f5f9',
  },
  settingInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  settingLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#334155',
  },
  settingDescription: {
    fontSize: '12px',
    color: '#94a3b8',
  },
  settingValue: {
    fontSize: '14px',
    color: '#64748b',
  },
  settingInput: {
    padding: '10px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    minWidth: '220px',
    outline: 'none',
    transition: 'border-color 0.15s ease',
  },
  settingSelect: {
    padding: '10px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    minWidth: '220px',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    outline: 'none',
  },
  contentHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  saveButton: {
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
  },
  saveButtonDisabled: {
    backgroundColor: '#94a3b8',
    cursor: 'not-allowed',
  },
  settingsSaveMessage: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
  },
  settingsSaveMessageSuccess: {
    backgroundColor: '#dcfce7',
    color: '#166534',
    border: '1px solid #bbf7d0',
  },
  settingsSaveMessageError: {
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    border: '1px solid #fecaca',
  },
  settingsSaveMessageClose: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    padding: '0 4px',
    lineHeight: '1',
    color: 'inherit',
    opacity: 0.7,
  },
  codeValue: {
    fontSize: '13px',
    color: '#64748b',
    backgroundColor: '#f1f5f9',
    padding: '4px 8px',
    borderRadius: '4px',
    fontFamily: 'monospace',
  },
  badge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: '500',
  },
  badgeEnabled: {
    backgroundColor: '#dcfce7',
    color: '#166534',
  },
  badgeDisabled: {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
  },
  badgeProduction: {
    backgroundColor: '#dbeafe',
    color: '#1e40af',
  },
  badgeDevelopment: {
    backgroundColor: '#fef9c3',
    color: '#854d0e',
  },
  // Team Management styles
  teamHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  teamCount: {
    color: '#94a3b8',
    fontWeight: '500',
  },
  inviteButton: {
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  inviteForm: {
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '20px',
    border: '1px solid #e2e8f0',
  },
  inviteFormHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  inviteFormTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b',
    margin: 0,
  },
  closeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '24px',
    color: '#64748b',
    cursor: 'pointer',
    padding: '0 4px',
    lineHeight: '1',
  },
  formGroup: {
    marginBottom: '16px',
  },
  formLabel: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#334155',
    marginBottom: '6px',
  },
  formInput: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  formSelect: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    outline: 'none',
    boxSizing: 'border-box',
  },
  formError: {
    color: '#dc2626',
    fontSize: '14px',
    marginBottom: '16px',
  },
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
  },
  cancelButton: {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '10px 16px',
    fontSize: '14px',
    color: '#475569',
    cursor: 'pointer',
  },
  submitButton: {
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  submitButtonDisabled: {
    backgroundColor: '#94a3b8',
    cursor: 'not-allowed',
  },
  teamFilters: {
    display: 'flex',
    gap: '12px',
    marginBottom: '20px',
  },
  searchInput: {
    flex: 1,
    padding: '10px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
  },
  filterSelect: {
    padding: '10px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    outline: 'none',
    minWidth: '140px',
  },
  teamLoading: {
    padding: '40px',
    textAlign: 'center',
    color: '#64748b',
  },
  teamError: {
    padding: '12px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '8px',
    fontSize: '14px',
    marginTop: '12px',
  },
  emptyState: {
    padding: '40px',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '12px',
  },
  emptyText: {
    color: '#64748b',
    fontSize: '14px',
    marginBottom: '12px',
  },
  clearFiltersButton: {
    backgroundColor: 'transparent',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '14px',
    color: '#3b82f6',
    cursor: 'pointer',
  },
  teamList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  teamMember: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '16px',
    borderRadius: '12px',
    backgroundColor: 'rgba(93, 86, 78, 0.02)',
    border: '1px solid rgba(93, 86, 78, 0.06)',
  },
  memberInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    flex: 1,
  },
  memberAvatar: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: '600',
    color: 'white',
  },
  memberDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  memberName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#3D3A36',
  },
  memberEmail: {
    fontSize: '12px',
    color: '#9B9285',
  },
  memberMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  memberRoleContainer: {
    textAlign: 'right' as const,
    marginRight: '12px',
  },
  roleBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: '600',
  },
  roleBadgeButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: '600',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s ease',
  },
  editRoleIcon: {
    fontSize: '10px',
    opacity: 0.7,
  },
  editRoleCurrentInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '16px',
    borderRadius: '12px',
    backgroundColor: 'rgba(93, 86, 78, 0.04)',
    marginBottom: '20px',
  },
  editRoleCurrentDetails: {
    flex: 1,
  },
  editRoleCurrentName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#3D3A36',
    margin: 0,
  },
  editRoleCurrentEmail: {
    fontSize: '12px',
    color: '#9B9285',
    margin: 0,
  },
  currentRoleIndicator: {
    fontSize: '10px',
    fontWeight: '500',
    color: '#64748b',
    backgroundColor: '#f1f5f9',
    padding: '2px 6px',
    borderRadius: '4px',
  },
  memberStatus: {
    display: 'block',
    fontSize: '11px',
    color: '#9B9285',
    marginTop: '6px',
  },
  removeButton: {
    padding: '8px 12px',
    borderRadius: '6px',
    backgroundColor: 'rgba(201, 140, 130, 0.1)',
    border: '1px solid rgba(201, 140, 130, 0.2)',
    fontSize: '12px',
    color: '#8B5A50',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    marginTop: '20px',
    paddingTop: '16px',
    borderTop: '1px solid #f1f5f9',
  },
  pageButton: {
    padding: '8px 16px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    fontSize: '14px',
    cursor: 'pointer',
    color: '#334155',
  },
  pageButtonDisabled: {
    backgroundColor: '#f8fafc',
    color: '#94a3b8',
    cursor: 'not-allowed',
  },
  pageInfo: {
    fontSize: '14px',
    color: '#64748b',
  },
  // Developer Tools styles
  devToolsMessage: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
  },
  devToolsMessageSuccess: {
    backgroundColor: '#dcfce7',
    color: '#166534',
    border: '1px solid #bbf7d0',
  },
  devToolsMessageError: {
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    border: '1px solid #fecaca',
  },
  devToolsMessageClose: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    padding: '0 4px',
    lineHeight: '1',
    color: 'inherit',
    opacity: 0.7,
  },
  devToolsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '16px',
  },
  devToolCard: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  devToolCardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
  },
  devToolCardIcon: {
    fontSize: '20px',
  },
  devToolCardTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b',
  },
  devToolCardDescription: {
    fontSize: '13px',
    color: '#64748b',
    marginBottom: '16px',
    lineHeight: '1.5',
  },
  devToolButton: {
    width: '100%',
    backgroundColor: '#f59e0b',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  devToolButtonDisabled: {
    backgroundColor: '#94a3b8',
    cursor: 'not-allowed',
  },
  // Security Note styles
  securityNote: {
    marginTop: '24px',
    padding: '16px',
    borderRadius: '12px',
    backgroundColor: 'rgba(90, 106, 138, 0.06)',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },
  securityNoteIcon: {
    fontSize: '18px',
  },
  securityNoteTitle: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#3D3A36',
    margin: '0 0 4px 0',
  },
  securityNoteText: {
    fontSize: '12px',
    color: '#706A62',
    margin: 0,
    lineHeight: '1.5',
  },
  // Modal styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(61, 58, 54, 0.6)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  inviteModalContainer: {
    background: 'white',
    borderRadius: '20px',
    width: '500px',
    maxHeight: '90vh',
    overflow: 'hidden',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  },
  verificationModalContainer: {
    background: 'white',
    borderRadius: '20px',
    width: '440px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  },
  modalHeader: {
    padding: '24px',
    borderBottom: '1px solid rgba(93, 86, 78, 0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  verificationModalHeader: {
    padding: '24px',
    borderBottom: '1px solid rgba(93, 86, 78, 0.08)',
    textAlign: 'center' as const,
  },
  verificationIconCircle: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
    fontSize: '28px',
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: '0 0 4px 0',
  },
  modalSubtitle: {
    fontSize: '13px',
    color: '#9B9285',
    margin: 0,
  },
  modalCloseButton: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    border: 'none',
    background: 'rgba(93, 86, 78, 0.08)',
    cursor: 'pointer',
    fontSize: '18px',
    color: '#706A62',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalBody: {
    padding: '24px',
  },
  modalFormGroup: {
    marginBottom: '20px',
  },
  modalFormLabel: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#5C564E',
    display: 'block',
    marginBottom: '8px',
  },
  modalFormInput: {
    width: '100%',
    padding: '14px 16px',
    borderRadius: '10px',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    fontSize: '14px',
    fontFamily: 'inherit',
    boxSizing: 'border-box' as const,
    outline: 'none',
  },
  roleOptionsContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '10px',
  },
  roleOption: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 16px',
    borderRadius: '10px',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    cursor: 'pointer',
    transition: 'border-color 0.15s ease',
  },
  roleOptionSelected: {
    borderColor: '#3D3A36',
  },
  roleRadio: {
    width: '18px',
    height: '18px',
    accentColor: '#3D3A36',
  },
  roleOptionContent: {
    flex: 1,
  },
  roleOptionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '2px',
  },
  roleOptionBadge: {
    padding: '3px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 600,
  },
  roleOptionDescription: {
    fontSize: '12px',
    color: '#9B9285',
    margin: 0,
  },
  securityNotice: {
    padding: '14px',
    borderRadius: '10px',
    background: 'rgba(232, 200, 140, 0.1)',
    border: '1px solid rgba(232, 200, 140, 0.2)',
  },
  securityNoticeContent: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
  },
  securityNoticeIcon: {
    fontSize: '16px',
  },
  securityNoticeTitle: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#9B7B3C',
    margin: '0 0 4px 0',
  },
  securityNoticeText: {
    fontSize: '12px',
    color: '#706A62',
    margin: 0,
  },
  modalFooter: {
    padding: '16px 24px',
    borderTop: '1px solid rgba(93, 86, 78, 0.08)',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
  },
  modalCancelButton: {
    padding: '12px 20px',
    borderRadius: '10px',
    background: 'white',
    border: '1px solid rgba(93, 86, 78, 0.15)',
    fontSize: '14px',
    fontWeight: 500,
    color: '#5C564E',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  modalSubmitButton: {
    padding: '12px 24px',
    borderRadius: '10px',
    background: '#3D3A36',
    border: 'none',
    fontSize: '14px',
    fontWeight: 600,
    color: 'white',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  // Verification Modal styles
  verificationCodeSection: {
    marginBottom: '24px',
  },
  verificationCodeLabel: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#5C564E',
    display: 'block',
    marginBottom: '12px',
    textAlign: 'center' as const,
  },
  verificationCodeInputs: {
    display: 'flex',
    justifyContent: 'center',
    gap: '10px',
  },
  verificationCodeInput: {
    width: '48px',
    height: '56px',
    borderRadius: '10px',
    border: '2px solid rgba(93, 86, 78, 0.15)',
    fontSize: '24px',
    fontWeight: 600,
    textAlign: 'center' as const,
    fontFamily: 'inherit',
    outline: 'none',
  },
  resendCodeText: {
    fontSize: '12px',
    color: '#9B9285',
    textAlign: 'center' as const,
    marginBottom: '20px',
  },
  resendCodeButton: {
    background: 'none',
    border: 'none',
    color: '#3b82f6',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '12px',
  },
  verifyButton: {
    width: '100%',
    padding: '14px',
    borderRadius: '10px',
    background: '#3D3A36',
    border: 'none',
    fontSize: '14px',
    fontWeight: 600,
    color: 'white',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  verifyButtonDisabled: {
    background: '#94a3b8',
    cursor: 'not-allowed',
  },
  // Success step styles
  successCard: {
    padding: '16px',
    borderRadius: '12px',
    background: 'rgba(152, 184, 154, 0.1)',
    marginBottom: '20px',
  },
  successCardContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  successAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: 'rgba(90, 138, 94, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 600,
    color: '#5A8A5E',
  },
  successInfo: {
    flex: 1,
  },
  successName: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: 0,
  },
  successEmail: {
    fontSize: '12px',
    color: '#9B9285',
    margin: 0,
  },
  successRoleBadge: {
    padding: '4px 10px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: 600,
  },
  successInstructions: {
    fontSize: '13px',
    color: '#706A62',
    textAlign: 'center' as const,
    marginBottom: '20px',
  },
  doneButton: {
    width: '100%',
    padding: '14px',
    borderRadius: '10px',
    background: '#3D3A36',
    border: 'none',
    fontSize: '14px',
    fontWeight: 600,
    color: 'white',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  // Toggle button styles
  toggleButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '6px 12px 6px 6px',
    borderRadius: '20px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
  },
  toggleButtonEnabled: {
    backgroundColor: '#dcfce7',
  },
  toggleButtonDisabled: {
    backgroundColor: '#f1f5f9',
  },
  toggleKnob: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    transition: 'all 0.2s ease',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  toggleKnobEnabled: {
    backgroundColor: '#22c55e',
  },
  toggleKnobDisabled: {
    backgroundColor: '#94a3b8',
  },
  toggleLabel: {
    fontSize: '12px',
    fontWeight: 500,
  },
  // Section header styles
  sectionHeader: {
    marginTop: '32px',
    marginBottom: '16px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1e293b',
    margin: '0 0 4px 0',
  },
  sectionDescription: {
    fontSize: '13px',
    color: '#64748b',
    margin: 0,
  },
  // Integrations styles
  integrationsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '16px',
  },
  integrationCard: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
  },
  integrationHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },
  integrationIcon: {
    fontSize: '24px',
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f8fafc',
    borderRadius: '10px',
    flexShrink: 0,
  },
  integrationInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  integrationName: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1e293b',
  },
  integrationDescription: {
    fontSize: '12px',
    color: '#64748b',
    lineHeight: '1.4',
  },
  integrationButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '10px 16px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    fontFamily: 'inherit',
    border: 'none',
  },
  integrationButtonConnected: {
    backgroundColor: '#dcfce7',
    color: '#166534',
  },
  integrationButtonDisconnected: {
    backgroundColor: '#3b82f6',
    color: '#ffffff',
  },
  integrationStatusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#22c55e',
  },
  // Integration meta info (shown when connected)
  integrationMeta: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
    padding: '12px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    marginTop: '-8px',
  },
  integrationMetaText: {
    fontSize: '11px',
    color: '#64748b',
    fontFamily: 'monospace',
  },
  // Integration modal styles
  integrationModalIcon: {
    width: '64px',
    height: '64px',
    backgroundColor: '#f8fafc',
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
  },
  integrationModalIconEmoji: {
    fontSize: '32px',
  },
  integrationModalDescription: {
    fontSize: '14px',
    color: '#64748b',
    textAlign: 'center' as const,
    marginBottom: '24px',
    lineHeight: '1.5',
  },
  integrationHelpLink: {
    marginBottom: '16px',
    textAlign: 'center' as const,
  },
  integrationHelpLinkAnchor: {
    fontSize: '13px',
    color: '#3b82f6',
    textDecoration: 'none',
  },
};

export default SettingsPage;
